import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import {
  getAggregatedItems,
  getCharacterItems,
  getDivineOrbHistory,
  getDivineOrbsPerHour,
  getDivineOrbRateHistory,
  getBulkDivineOrbData,
} from '../../services/itemService';
import type {
  ApiError,
  ApiItemResponse,
  DivineOrbHistoryDto,
  DivineOrbPerHourDto,
  DivineOrbsGained24hDto,
  DivineOrbRateHistoryDto,
  BulkDivineOrbDataResponse,
} from '../../services/itemService';
import type { ItemPropertiesDto } from '../../types/item.types';
import type { RootState } from '../store';

// Define the Item type based on backend entity
export interface Item {
  id: string;
  name: string;
  item_type?: string;
  properties?: ItemPropertiesDto;
  quantity: number;
  item_slot?: string;
  inventory_type?: string;
  game: string;
  league: string;
}

interface ItemsState {
  items: Item[];
  characterItems: Item[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  characterItemsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null | ApiError;
  characterItemsError: string | null | ApiError;
  selectedCharacterId: string | null;

  // New state for divine orb history and per hour data
  divineOrbHistory: Record<string, DivineOrbHistoryDto[]>;
  divineOrbHistoryStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  divineOrbHistoryError: string | null | ApiError;

  divineOrbsPerHour: Record<string, DivineOrbPerHourDto>;
  divineOrbsPerHourStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  divineOrbsPerHourError: string | null | ApiError;

  divineOrbsGained24h: Record<string, DivineOrbsGained24hDto>;
  divineOrbsGained24hStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  divineOrbsGained24hError: string | null | ApiError;

  divineOrbRateHistory: Record<string, DivineOrbRateHistoryDto[]>;
  divineOrbRateHistoryStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  divineOrbRateHistoryError: string | null | ApiError;
}

const initialState: ItemsState = {
  items: [],
  characterItems: [],
  status: 'idle',
  characterItemsStatus: 'idle',
  error: null,
  characterItemsError: null,
  selectedCharacterId: null,

  // Initialize new state properties
  divineOrbHistory: {},
  divineOrbHistoryStatus: 'idle',
  divineOrbHistoryError: null,

  divineOrbsPerHour: {},
  divineOrbsPerHourStatus: 'idle',
  divineOrbsPerHourError: null,

  divineOrbsGained24h: {},
  divineOrbsGained24hStatus: 'idle',
  divineOrbsGained24hError: null,

  divineOrbRateHistory: {},
  divineOrbRateHistoryStatus: 'idle',
  divineOrbRateHistoryError: null,
};

// Async thunk for fetching items
export const fetchItems = createAsyncThunk<
  Item[], // Return type of the payload
  void, // First argument to the payload creator (no args for fetchItems)
  { rejectValue: ApiError; state: RootState } // Types for thunkAPI
>('items/fetchItems', async (_, thunkAPI) => {
  try {
    // Pass game and league to the API call to get aggregated items
    // Use unknown as an intermediate type for safe casting
    const response = await getAggregatedItems();
    // Cast the response to the appropriate type
    const apiResponse = response as unknown as ApiItemResponse[];

    // Map backend response to match our frontend Item interface
    return apiResponse.map(item => ({
      id: item.id,
      name: item.item_name || '', // Use item_name if name is not available
      item_type: item.item_type || '',
      properties: item.properties || {},
      quantity: item.quantity || 1,
      item_slot: item.item_slot || '',
      inventory_type: item.inventory_type || '',
      game: item.game,
      league: item.league,
    }));
  } catch (error: unknown) {
    const apiError: ApiError = {
      message:
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (error as { message?: string })?.message ||
        'Failed to fetch aggregated items',
      statusCode: (error as { response?: { status?: number } })?.response?.status,
      errors: (error as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors,
    };
    return thunkAPI.rejectWithValue(apiError);
  }
});

// Async thunk for fetching divine orb history for a character
export const fetchDivineOrbHistory = createAsyncThunk<
  { characterId: string; data: DivineOrbHistoryDto[] }, // Return type with character ID for mapping
  string, // First argument to the payload creator (characterId)
  { rejectValue: ApiError; state: RootState } // Types for thunkAPI
>('items/fetchDivineOrbHistory', async (characterId, thunkAPI) => {
  try {
    // Call the API
    const response = await getDivineOrbHistory(characterId);
    // Return both the character ID and the data for storing in the state
    return {
      characterId,
      data: response,
    };
  } catch (error: unknown) {
    const apiError: ApiError = {
      message:
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (error as { message?: string })?.message ||
        'Failed to fetch divine orb history',
      statusCode: (error as { response?: { status?: number } })?.response?.status,
      errors: (error as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors,
    };
    return thunkAPI.rejectWithValue(apiError);
  }
});

// Async thunk for fetching divine orbs per hour for a character
export const fetchDivineOrbsPerHour = createAsyncThunk<
  { characterId: string; data: DivineOrbPerHourDto }, // Return type with character ID for mapping
  string, // First argument to the payload creator (characterId)
  { rejectValue: ApiError; state: RootState } // Types for thunkAPI
>('items/fetchDivineOrbsPerHour', async (characterId, thunkAPI) => {
  try {
    // Call the API
    const response = await getDivineOrbsPerHour(characterId);

    // Return both the character ID and the data for storing in the state
    return {
      characterId,
      data: response,
    };
  } catch (error: unknown) {
    console.error('[DEBUG] Error fetching divine orbs per hour:', error);
    const apiError: ApiError = {
      message:
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (error as { message?: string })?.message ||
        'Failed to fetch divine orbs per hour',
      statusCode: (error as { response?: { status?: number } })?.response?.status,
      errors: (error as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors,
    };
    return thunkAPI.rejectWithValue(apiError);
  }
});

// Async thunk for fetching divine orb rate history for a character
export const fetchDivineOrbRateHistory = createAsyncThunk<
  { characterId: string; data: DivineOrbRateHistoryDto[] }, // Return type with character ID for mapping
  string, // First argument to the payload creator (characterId)
  { rejectValue: ApiError; state: RootState } // Types for thunkAPI
>('items/fetchDivineOrbRateHistory', async (characterId, thunkAPI) => {
  try {
    // Call the API
    const response = await getDivineOrbRateHistory(characterId);
    // Return both the character ID and the data for storing in the state
    return {
      characterId,
      data: response,
    };
  } catch (error: unknown) {
    const apiError: ApiError = {
      message:
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (error as { message?: string })?.message ||
        'Failed to fetch divine orb rate history',
      statusCode: (error as { response?: { status?: number } })?.response?.status,
      errors: (error as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors,
    };
    return thunkAPI.rejectWithValue(apiError);
  }
});

// Async thunk for fetching divine orb data in bulk for multiple characters
export const fetchBulkDivineOrbData = createAsyncThunk<
  BulkDivineOrbDataResponse,
  string[], // Array of character IDs
  { rejectValue: ApiError; state: RootState }
>('items/fetchBulkDivineOrbData', async (characterIds, thunkAPI) => {
  try {
    // Call the bulk API
    const response = await getBulkDivineOrbData(characterIds);

    return response;
  } catch (error: unknown) {
    console.error('[DEBUG] Error fetching bulk divine orb data:', error);
    const apiError: ApiError = {
      message:
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (error as { message?: string })?.message ||
        'Failed to fetch bulk divine orb data',
      statusCode: (error as { response?: { status?: number } })?.response?.status,
      errors: (error as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors,
    };
    return thunkAPI.rejectWithValue(apiError);
  }
});

// Async thunk for fetching character items
export const fetchCharacterItems = createAsyncThunk<
  Item[], // Return type of the payload
  string, // First argument to the payload creator (characterId)
  { rejectValue: ApiError; state: RootState } // Types for thunkAPI
>('items/fetchCharacterItems', async (characterId, thunkAPI) => {
  try {
    // Get selected game and league from filter state
    const state = thunkAPI.getState();
    const selectedGame = state.filter.selectedGame;
    const selectedLeague = state.filter.selectedLeague;

    console.log(`[DEBUG] Fetching items for character ID: ${characterId}, game: ${selectedGame}, league: ${selectedLeague}`);

    // Pass characterId, game and league to the API call
    const response = await getCharacterItems(characterId);

    // Cast the response to the appropriate type
    const apiResponse = response as unknown as ApiItemResponse[];

    // Map backend response to match our frontend Item interface
    const mappedItems = apiResponse.map(item => ({
      id: item.id,
      name: item.item_name || '', // Use item_name if name is not available
      item_type: item.item_type || '',
      properties: item.properties || {},
      quantity: item.quantity || 1,
      item_slot: item.item_slot || '',
      inventory_type: item.inventory_type || '',
      game: item.game,
      league: item.league,
    }));

    return mappedItems;
  } catch (error: unknown) {
    console.error('[DEBUG] Error fetching character items:', error);
    const apiError: ApiError = {
      message:
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (error as { message?: string })?.message ||
        'Failed to fetch character items',
      statusCode: (error as { response?: { status?: number } })?.response?.status,
      errors: (error as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors,
    };
    return thunkAPI.rejectWithValue(apiError);
  }
});

export const itemsSlice = createSlice({
  name: 'items',
  initialState,
  reducers: {
    clearItemsError: state => {
      state.error = null;
      state.status = 'idle'; // Reset status if needed
    },
    clearCharacterItemsError: state => {
      state.characterItemsError = null;
      state.characterItemsStatus = 'idle';
    },
    setSelectedCharacterId: (state, action: PayloadAction<string | null>) => {
      state.selectedCharacterId = action.payload;
    },
    clearDivineOrbHistoryError: state => {
      state.divineOrbHistoryError = null;
      state.divineOrbHistoryStatus = 'idle';
    },
    clearDivineOrbsPerHourError: state => {
      state.divineOrbsPerHourError = null;
      state.divineOrbsPerHourStatus = 'idle';
    },
    clearDivineOrbRateHistoryError: state => {
      state.divineOrbRateHistoryError = null;
      state.divineOrbRateHistoryStatus = 'idle';
    },
    // Potentially add other reducers here if needed, e.g., addItem, updateItem, deleteItem
  },
  extraReducers: builder => {
    builder
      // Fetch Items Cases
      .addCase(fetchItems.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchItems.fulfilled, (state, action: PayloadAction<Item[]>) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchItems.rejected, (state, action: PayloadAction<ApiError | undefined>) => {
        state.status = 'failed';
        if (action.payload) {
          state.error = action.payload;
        } else {
          state.error = { message: 'An unknown error occurred' };
        }
      })

      // Fetch Character Items Cases
      .addCase(fetchCharacterItems.pending, state => {
        state.characterItemsStatus = 'loading';
        state.characterItemsError = null;
      })
      .addCase(fetchCharacterItems.fulfilled, (state, action: PayloadAction<Item[]>) => {
        state.characterItemsStatus = 'succeeded';

        // Ensure we're storing an array, even if empty
        if (Array.isArray(action.payload)) {
          state.characterItems = action.payload;
        } else {
          console.error('[DEBUG] Expected array payload but received:', typeof action.payload);
          // Initialize as empty array if not an array
          state.characterItems = [];
        }
      })
      .addCase(fetchCharacterItems.rejected, (state, action: PayloadAction<ApiError | undefined>) => {
        state.characterItemsStatus = 'failed';
        if (action.payload) {
          state.characterItemsError = action.payload;
        } else {
          state.characterItemsError = {
            message: 'An unknown error occurred',
          };
        }
      })

      // Fetch Divine Orb History Cases
      .addCase(fetchDivineOrbHistory.pending, state => {
        state.divineOrbHistoryStatus = 'loading';
        state.divineOrbHistoryError = null;
      })
      .addCase(
        fetchDivineOrbHistory.fulfilled,
        (
          state,
          action: PayloadAction<{
            characterId: string;
            data: DivineOrbHistoryDto[];
          }>,
        ) => {
          state.divineOrbHistoryStatus = 'succeeded';

          // Store the history data mapped by character ID
          state.divineOrbHistory = {
            ...state.divineOrbHistory,
            [action.payload.characterId]: action.payload.data,
          };
        },
      )
      .addCase(fetchDivineOrbHistory.rejected, (state, action: PayloadAction<ApiError | undefined>) => {
        state.divineOrbHistoryStatus = 'failed';
        if (action.payload) {
          state.divineOrbHistoryError = action.payload;
        } else {
          state.divineOrbHistoryError = {
            message: 'An unknown error occurred',
          };
        }
      })

      // Fetch Divine Orbs Per Hour Cases
      .addCase(fetchDivineOrbsPerHour.pending, state => {
        state.divineOrbsPerHourStatus = 'loading';
        state.divineOrbsPerHourError = null;
      })
      .addCase(
        fetchDivineOrbsPerHour.fulfilled,
        (
          state,
          action: PayloadAction<{
            characterId: string;
            data: DivineOrbPerHourDto;
          }>,
        ) => {
          state.divineOrbsPerHourStatus = 'succeeded';

          // Store the per hour data mapped by character ID
          state.divineOrbsPerHour = {
            ...state.divineOrbsPerHour,
            [action.payload.characterId]: action.payload.data,
          };
        },
      )
      .addCase(fetchDivineOrbsPerHour.rejected, (state, action: PayloadAction<ApiError | undefined>) => {
        state.divineOrbsPerHourStatus = 'failed';
        if (action.payload) {
          state.divineOrbsPerHourError = action.payload;
        } else {
          state.divineOrbsPerHourError = {
            message: 'An unknown error occurred',
          };
        }
      })

      // Fetch Divine Orb Rate History Cases
      .addCase(fetchDivineOrbRateHistory.pending, state => {
        state.divineOrbRateHistoryStatus = 'loading';
        state.divineOrbRateHistoryError = null;
      })
      .addCase(
        fetchDivineOrbRateHistory.fulfilled,
        (
          state,
          action: PayloadAction<{
            characterId: string;
            data: DivineOrbRateHistoryDto[];
          }>,
        ) => {
          state.divineOrbRateHistoryStatus = 'succeeded';

          // Store the rate history data mapped by character ID
          state.divineOrbRateHistory = {
            ...state.divineOrbRateHistory,
            [action.payload.characterId]: action.payload.data,
          };
        },
      )
      .addCase(fetchDivineOrbRateHistory.rejected, (state, action: PayloadAction<ApiError | undefined>) => {
        state.divineOrbRateHistoryStatus = 'failed';
        if (action.payload) {
          state.divineOrbRateHistoryError = action.payload;
        } else {
          state.divineOrbRateHistoryError = {
            message: 'An unknown error occurred',
          };
        }
      })

      // Fetch Bulk Divine Orb Data Cases
      .addCase(fetchBulkDivineOrbData.pending, state => {
        state.divineOrbHistoryStatus = 'loading';
        state.divineOrbHistoryError = null;
        state.divineOrbsPerHourStatus = 'loading';
        state.divineOrbsPerHourError = null;
        state.divineOrbsGained24hStatus = 'loading';
        state.divineOrbsGained24hError = null;
      })
      .addCase(fetchBulkDivineOrbData.fulfilled, (state, action: PayloadAction<BulkDivineOrbDataResponse>) => {
        // Update divine orb history, divine orbs per hour, and divine orbs gained 24h data
        state.divineOrbHistory = {
          ...state.divineOrbHistory,
          ...action.payload.divineOrbHistory,
        };

        state.divineOrbsPerHour = {
          ...state.divineOrbsPerHour,
          ...action.payload.divineOrbsPerHour,
        };

        state.divineOrbsGained24h = {
          ...state.divineOrbsGained24h,
          ...action.payload.divineOrbsGained24h,
        };

        // Mark all states as succeeded
        state.divineOrbHistoryStatus = 'succeeded';
        state.divineOrbsPerHourStatus = 'succeeded';
        state.divineOrbsGained24hStatus = 'succeeded';
      })
      .addCase(fetchBulkDivineOrbData.rejected, (state, action: PayloadAction<ApiError | undefined>) => {
        state.divineOrbHistoryStatus = 'failed';
        state.divineOrbsPerHourStatus = 'failed';
        state.divineOrbsGained24hStatus = 'failed';

        const errorData = action.payload || {
          message: 'An unknown error occurred',
        };

        state.divineOrbHistoryError = errorData;
        state.divineOrbsPerHourError = errorData;
        state.divineOrbsGained24hError = errorData;
      });
  },
});

export const {
  clearItemsError,
  clearCharacterItemsError,
  setSelectedCharacterId,
  clearDivineOrbHistoryError,
  clearDivineOrbsPerHourError,
  clearDivineOrbRateHistoryError,
} = itemsSlice.actions;

// Selectors
export const selectAllItems = (state: RootState): Item[] => state.items.items;
export const selectItemsStatus = (state: RootState): string => state.items.status;
export const selectItemsError = (state: RootState): string | null | ApiError => state.items.error;

// Character items selectors
export const selectCharacterItems = (state: RootState): Item[] => state.items.characterItems;
export const selectCharacterItemsStatus = (state: RootState): string => state.items.characterItemsStatus;
export const selectCharacterItemsError = (state: RootState): string | null | ApiError => state.items.characterItemsError;
export const selectSelectedCharacterId = (state: RootState): string | null => state.items.selectedCharacterId;

// Divine Orb History selectors
export const selectDivineOrbHistory = (state: RootState): Record<string, DivineOrbHistoryDto[]> => state.items.divineOrbHistory;
export const selectDivineOrbHistoryStatus = (state: RootState): string => state.items.divineOrbHistoryStatus;
export const selectDivineOrbHistoryError = (state: RootState): string | null | ApiError => state.items.divineOrbHistoryError;
export const selectDivineOrbHistoryByCharacterId = (state: RootState, characterId: string): DivineOrbHistoryDto[] | undefined =>
  state.items.divineOrbHistory[characterId];

// Divine Orbs Per Hour selectors
export const selectDivineOrbsPerHour = (state: RootState): Record<string, DivineOrbPerHourDto> => state.items.divineOrbsPerHour;
export const selectDivineOrbsPerHourStatus = (state: RootState): string => state.items.divineOrbsPerHourStatus;
export const selectDivineOrbsPerHourError = (state: RootState): string | null | ApiError => state.items.divineOrbsPerHourError;
export const selectDivineOrbsPerHourByCharacterId = (state: RootState, characterId: string): DivineOrbPerHourDto | undefined =>
  state.items.divineOrbsPerHour[characterId];

// Divine Orbs Gained 24h selectors
export const selectDivineOrbsGained24h = (state: RootState): Record<string, DivineOrbsGained24hDto> => state.items.divineOrbsGained24h;
export const selectDivineOrbsGained24hStatus = (state: RootState): string => state.items.divineOrbsGained24hStatus;
export const selectDivineOrbsGained24hError = (state: RootState): string | null | ApiError => state.items.divineOrbsGained24hError;
export const selectDivineOrbsGained24hByCharacterId = (state: RootState, characterId: string): DivineOrbsGained24hDto | undefined =>
  state.items.divineOrbsGained24h[characterId];

// Divine Orb Rate History selectors
export const selectDivineOrbRateHistory = (state: RootState): Record<string, DivineOrbRateHistoryDto[]> => state.items.divineOrbRateHistory;
export const selectDivineOrbRateHistoryStatus = (state: RootState): string => state.items.divineOrbRateHistoryStatus;
export const selectDivineOrbRateHistoryError = (state: RootState): string | null | ApiError => state.items.divineOrbRateHistoryError;
export const selectDivineOrbRateHistoryByCharacterId = (state: RootState, characterId: string): DivineOrbRateHistoryDto[] | undefined =>
  state.items.divineOrbRateHistory[characterId];

export default itemsSlice.reducer;
